import React, { useMemo } from "react";
import { FloatingHint } from "@/components/FloatingHint";
import InlineAlert from "@scripts/react/components/InlineAlert";
import Button from "@scripts/react/components/Button";
import { UploadLimitErrorType } from "@/constants/media";

interface UploadLimitHintProps {
    uploadLimitError: UploadLimitErrorType | null;
    variant?: "floating" | "inline";
    hideIcon?: boolean;
    onUploadAgain?: () => void;
    onDismiss?: () => void;
}

const getErrorMessage = (errorType: UploadLimitErrorType | null) => {
    switch (errorType) {
        case UploadLimitErrorType.TOO_MANY_IMAGES:
            return (
                <p>
                    <strong>Too many images. </strong>You can upload a maximum of 10 images at a
                    time.
                </p>
            );
        case UploadLimitErrorType.TOO_MANY_VIDEOS:
            return (
                <p>
                    <strong>Too many videos.</strong>You can upload <strong>only 1 video</strong> at
                    a time.
                </p>
            );
        case UploadLimitErrorType.MIXED_CONTENT:
            return (
                <p>
                    <strong>You cannot upload images and videos together.</strong> Please select{" "}
                    <strong>up to 10 images</strong> or <strong>1 video</strong> and try again.
                </p>
            );
        case UploadLimitErrorType.FILE_TOO_LARGE:
            return (
                <p>
                    <strong>File too large.</strong> One or more files exceed the{" "}
                    <strong>100MB size limit</strong>.
                </p>
            );
        default:
            return null;
    }
};

export const UploadLimitHint: React.FC<UploadLimitHintProps> = ({
    uploadLimitError,
    variant = "floating",
    hideIcon = false,
    onUploadAgain,
    onDismiss
}) => {
    const errorMessage = useMemo(() => getErrorMessage(uploadLimitError), [uploadLimitError]);

    if (!uploadLimitError) return null;

    if (variant === "inline") {
        return (
            <InlineAlert
                level="danger"
                type="primary"
                dense={true}
                textClassName="text-left"
                icon={hideIcon ? "hidden" : undefined}
                contentClassName={hideIcon ? "gap-0" : undefined}
            >
                {errorMessage}
            </InlineAlert>
        );
    }

    if (!errorMessage) return null;

    return (
        <FloatingHint
            isVisible={!!uploadLimitError}
            isDismissable={true}
            lightMode={true}
            className="!border-destructive !bg-pink-50 text-sm !text-destructive"
            onDismiss={onDismiss}
            message={
                <div className="flex items-center gap-3">
                    <i className="fas fa-exclamation-circle flex-shrink-0" />
                    <span>{errorMessage}</span>
                    {onUploadAgain && (
                        <Button type="secondary" size="xsmall" onClick={onUploadAgain}>
                            Upload Again
                        </Button>
                    )}
                </div>
            }
        />
    );
};
